import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth } from "../../../../contexts/UserContext";
import { formatarDataHora } from "../../../../utility/timezoneUtils";
import { extrairOrientacoes } from "../utils";
import { Trash2, Loader2 } from "lucide-react";
import api from "../../../../services/api";
import toast from "react-hot-toast";
import { toastDefs, toastDefsError } from "../../../../config/toastConfig";
import { comum } from "../../../../styles/Themes/light";
import {
  Cartao,
  Titulo,
  TituloAcoes,
  Conteudo,
  Rotulo,
  ValorDestacado,
  BotaoPrimario,
  BotaoIcone,
  Arquivo,
  ArquivoIcone,
  ArquivoConteudo,
  ArquivoNome,
  ArquivoTamanho,
  ArquivoAcao,
  Linha,
  Coluna,
  Selo,
  Timestamp,
} from "../styles";
import { IconAlvo, IconClock, IconDocument, IconExternalLink, IconPonto, IconPencil } from "../../../icons/IconesSistema";
import { ArquivoComMargem, ArquivosContainer, ContainerVazio, IconeDocumentoVazio, Paragrafo, ParagrafoInfo, ParagrafoMuted, SeloContainer } from "./styles";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatFileSize } from "../../../../utility/byteSizeUtils";

const FileXCorner = ({ size = 24, color = "currentColor" }: { size?: number; color?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v5"/>
        <path d="M14 2v5a1 1 0 0 0 1 1h5"/>
        <path d="m15 17 5 5"/>
        <path d="m20 17-5 5"/>
    </svg>
);

function mapearTipoResponsavel(tipo?: string): string | null {
    if (!tipo) {
        return null;
    }
    
    const tipoLower = tipo.toLowerCase();
    
    switch (tipoLower) {
        case 'operador':
            return 'Operador';
        case 'solicitante':
            return 'Solicitante';
        case 'usuario':
        case 'profissional':
            return 'Profissional';
        default:
            return tipo;
    }
}

export default function OrientacoesCompletas({ demanda, onAdicionar, onEditar, loading = false, onNavigateToFile, onRecarregar }: any) {
  const history = useHistory();
  const { id: idDemanda } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [excluindoArquivo, setExcluindoArquivo] = useState<number | null>(null);

  const podeAdicionarOrientacao = user?.profile === 'admin' || user?.profile === 'operador';
  const podeEditarOrientacao = user?.profile === 'admin' || user?.profile === 'operador';
  const podeExcluirArquivo = user?.profile === 'admin' || user?.profile === 'operador';

  const { orientacoesCompletas } = useMemo(() => {
    const lista = Array.isArray(demanda?.orientacoes) ? demanda?.orientacoes : [];
    return extrairOrientacoes(lista as unknown[]);
  }, [demanda?.orientacoes]);

  const listaOrientacoes = orientacoesCompletas ?? [];

  const handleNavegarParaAnexos = (arquivo: any) => {
    if (onNavigateToFile && arquivo?.id_orientacao_arquivo) {
      onNavigateToFile(arquivo.id_orientacao_arquivo);
    } else if (idDemanda) {
      history.push(`/demandas/${idDemanda}/anexos`);
    }
  };

  const handleExcluirArquivo = async (e: React.MouseEvent, arquivo: any, orientacao: any) => {
    e.stopPropagation();
    
    if (!arquivo.id_orientacao_arquivo || !orientacao.id_orientacao) {
      return;
    }

    setExcluindoArquivo(arquivo.id_orientacao_arquivo);

    try {
      await api.delete(`orientacao/${orientacao.id_orientacao}/arquivos/${arquivo.id_orientacao_arquivo}`);
      toast.success('Arquivo removido com sucesso', toastDefs);
      
      if (onRecarregar) {
        onRecarregar();
      } else if (demanda?.id_proposta) {
        window.location.reload();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao remover arquivo';
      toast.error(errorMessage, toastDefsError);
    } finally {
      setExcluindoArquivo(null);
    }
  };

  const formatarDataRelativa = (data: string | Date | null | undefined): string => {
    if (!data) return "";
    try {
      const dataFormatada = new Date(data);
      if (Number.isNaN(dataFormatada.getTime())) return "";
      return formatDistanceToNow(dataFormatada, { 
        addSuffix: true, 
        locale: ptBR 
      });
    } catch (error) {
      return "";
    }
  };

  const dataAtualizacaoMaisRecente = useMemo(() => {
    if (listaOrientacoes.length === 0) return null;

    const datasModificacao = listaOrientacoes
      .map(orientacao => orientacao.modificado)
      .filter((data): data is string => !!data);

    if (datasModificacao.length === 0) return null;

    const maisRecente = datasModificacao.reduce((dataRecente, dataAtual) => {
      return new Date(dataAtual) > new Date(dataRecente) ? dataAtual : dataRecente;
    });

    return maisRecente;
  }, [listaOrientacoes]);

  const dataRelativa = formatarDataRelativa(dataAtualizacaoMaisRecente);

  if (loading) {
    return (
      <Cartao style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: '17px', right: '17px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
          <Skeleton width={80} height={36} borderRadius={6} />
          <Skeleton width={120} height={12} />
        </div>
        <Titulo>
          <Skeleton circle width={16} height={16} />
          <Skeleton width={180} height={18} />
        </Titulo>
        <Conteudo>
          <Skeleton width={60} height={20} borderRadius={12} style={{ marginBottom: '12px' }} />
          <Skeleton width={180} height={14} style={{ marginBottom: '8px' }} />
          <Skeleton width="100%" height={40} borderRadius={6} style={{ marginBottom: '16px' }} />
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <Skeleton width={120} height={14} style={{ marginBottom: '8px' }} />
              <Skeleton width="100%" height={40} borderRadius={6} />
            </div>
            <div style={{ flex: 1 }}>
              <Skeleton width={60} height={14} style={{ marginBottom: '8px' }} />
              <Skeleton width="100%" height={40} borderRadius={6} />
            </div>
          </div>
          <Skeleton width={140} height={14} style={{ marginBottom: '8px' }} />
          <Skeleton width="100%" height={40} borderRadius={6} style={{ marginBottom: '16px' }} />
          <Skeleton width={140} height={14} style={{ marginBottom: '8px' }} />
          <Skeleton width="100%" height={40} borderRadius={6} style={{ marginBottom: '16px' }} />
          <Skeleton width={220} height={14} style={{ marginBottom: '8px' }} />
          <Skeleton width="100%" height={40} borderRadius={6} style={{ marginBottom: '16px' }} />
        </Conteudo>
      </Cartao>
    );
  }

  if (listaOrientacoes.length === 0) {
    return (
      <Cartao>
        <Titulo>
          <IconAlvo size={18} />
          Orientações Completas
        </Titulo>
        <ContainerVazio>
          <IconeDocumentoVazio>
            <IconDocument size={28} />
          </IconeDocumentoVazio>
          <Paragrafo>Sem orientações completas</Paragrafo>
          <ParagrafoMuted>
            {podeAdicionarOrientacao 
              ? 'Esta demanda ainda não possui orientações completas cadastradas. Clique no botão abaixo para adicionar.'
              : 'Esta demanda ainda não possui orientações completas cadastradas.'}
          </ParagrafoMuted>
          {podeAdicionarOrientacao && (
            <BotaoPrimario type="button" onClick={onAdicionar}>
              <BotaoIcone>
                <IconDocument size={16} color="#fff" />
              </BotaoIcone>
              Adicionar Orientação Completa
            </BotaoPrimario>
          )}
        </ContainerVazio>
      </Cartao>
    );
  }

  return (
    <Cartao>
      <Titulo>
        <IconAlvo size={16} />
        Orientações Completas
        {(dataRelativa || podeEditarOrientacao) && (
          <TituloAcoes>
            {dataRelativa && (
              <Timestamp>
                <IconClock size={14} />
                {dataRelativa}
              </Timestamp>
            )}
            {podeEditarOrientacao && (
              <BotaoPrimario type="button" onClick={() => onEditar && listaOrientacoes[0] && onEditar(listaOrientacoes[0])}>
                <BotaoIcone>
                  <IconPencil size={16} color="#fff" />
                </BotaoIcone>
                Editar
              </BotaoPrimario>
            )}
          </TituloAcoes>
        )}
      </Titulo>

      {listaOrientacoes.map((orientacao: any, indice: number) => {
        const statusOrientacao = (orientacao.status || "").trim();

        return (
          <Conteudo key={orientacao.id_orientacao || `${indice}-${orientacao.parte_representada}`}>
            {statusOrientacao && (
              <SeloContainer>
                <Selo status={statusOrientacao}>
                  <IconPonto size={16} color={statusOrientacao === "Ciente" ? "var(--c22c55e)" : "var(--ceab308)"} />
                  {statusOrientacao}
                </Selo>
              </SeloContainer>
            )}

            <Rotulo>Qual parte representamos:</Rotulo>
            <ParagrafoInfo>{orientacao.parte_representada?.trim() || "--"}</ParagrafoInfo>

            <Linha>
              <Coluna>
                <Rotulo>Nome do advogado</Rotulo>
                <ParagrafoInfo>{orientacao.nome_advogado?.trim() || "--"}</ParagrafoInfo>
              </Coluna>
              <Coluna>
                <Rotulo>OAB</Rotulo>
                <ParagrafoInfo>{orientacao.oab_advogado?.trim() || "--"}</ParagrafoInfo>
              </Coluna>
            </Linha>

            <Rotulo>Nome do preposto</Rotulo>
            <ParagrafoInfo>{orientacao.nome_preposto?.trim() || "--"}</ParagrafoInfo>

            <Rotulo>Proposta de acordo</Rotulo>
            <ParagrafoInfo>{orientacao.proposta_acordo?.trim() || "--"}</ParagrafoInfo>

            <Rotulo>Requerimentos a realizar em audiência</Rotulo>
            <ParagrafoInfo>{orientacao.requerimentos?.trim() || "--"}</ParagrafoInfo>

            <Rotulo>Documentos a juntar</Rotulo>
            <ParagrafoInfo>{orientacao.documentos?.trim() || "--"}</ParagrafoInfo>

            {Array.isArray(orientacao.arquivos) && orientacao.arquivos.filter((a: any) => !a.excluido).length > 0 && (
              <ValorDestacado>
                <ArquivosContainer>
                  {orientacao.arquivos
                    .filter((arquivo: any) => !arquivo.excluido)
                    .map((arquivo: any) => {
                    const chaveArquivo = arquivo.id_orientacao_arquivo || `${arquivo.caminho}-${arquivo.id_orientacao}`;
                    const nomeArquivo = arquivo.caminho ? arquivo.caminho.split('/').pop() : undefined;
                    const estaExcluindo = excluindoArquivo === arquivo.id_orientacao_arquivo;
                    const isExcluido = arquivo.excluido === true;
                    return (
                      <ArquivoComMargem 
                        key={chaveArquivo} 
                        $isExcluido={isExcluido}
                        onClick={() => !isExcluido && handleNavegarParaAnexos(arquivo)}
                        style={{
                          border: isExcluido ? '1px solid rgba(239, 68, 68, 0.3)' : undefined,
                          opacity: isExcluido ? 0.75 : 1,
                          cursor: isExcluido ? 'default' : 'pointer'
                        }}
                      >
                          <ArquivoIcone>
                            {isExcluido ? (
                              <FileXCorner size={18} color="#EF4444" />
                            ) : (
                              <IconDocument size={18} />
                            )}
                          </ArquivoIcone>
                        <ArquivoConteudo>
                          <ArquivoNome style={{ fontStyle: isExcluido ? 'italic' : 'normal' }}>
                            {nomeArquivo || arquivo.caminho}
                          </ArquivoNome>
                          <ArquivoTamanho>
                            {arquivo.tamanho && (
                              <span style={{ fontSize: '12px', color: 'var(--c64748b)', marginRight: '8px' }}>
                                {formatFileSize(arquivo.tamanho)}
                              </span>
                            )}
                            {isExcluido ? (
                              <span style={{ fontSize: '11px', color: 'var(--c64748b)' }}>
                                Arquivo removido e não está mais disponível
                              </span>
                            ) : (
                              <>
                                Enviado por {arquivo.nome_responsavel || '—'} 
                                {arquivo.modificado && <span style={{ marginLeft: '6px' }}>{formatarDataHora(arquivo.modificado)}</span>}
                                {mapearTipoResponsavel(arquivo.tipo_responsavel) && 
                                  <span style={{ 
                                    marginLeft: '6px', 
                                    padding: '2px 6px', 
                                    borderRadius: '4px', 
                                    backgroundColor: 'rgb(252, 230, 230)', 
                                    color: 'rgb(239, 68, 68)', 
                                    fontSize: '10px', 
                                    fontWeight: 500 
                                  }}>
                                    {mapearTipoResponsavel(arquivo.tipo_responsavel)}
                                  </span>
                                }
                              </>
                            )}
                          </ArquivoTamanho>
                        </ArquivoConteudo>
                        <ArquivoAcao style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {isExcluido ? (
                            <div style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              fontSize: '10px',
                              fontWeight: 500,
                              color: 'var(--cef4444)',
                              whiteSpace: 'nowrap'
                            }}>
                              Removido
                            </div>
                          ) : (
                            <>
                              <IconExternalLink size={16} color="var(--c353a40)" />
                              {podeExcluirArquivo && (
                                <div
                                  onClick={(e) => handleExcluirArquivo(e, arquivo, orientacao)}
                                  style={{
                                    cursor: estaExcluindo ? 'not-allowed' : 'pointer',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    transition: 'all 0.2s ease',
                                    opacity: estaExcluindo ? 0.6 : 1
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!estaExcluindo) {
                                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                  title="Excluir arquivo"
                                >
                                  {estaExcluindo ? (
                                    <Loader2 size={16} color={comum.colors.primaryRed400} className="spinner" />
                                  ) : (
                                    <Trash2 size={16} color={comum.colors.primaryRed400} />
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </ArquivoAcao>
                      </ArquivoComMargem>
                    );
                  })}
                </ArquivosContainer>
              </ValorDestacado>
            )}

            <Rotulo>Testemunhas a ouvir</Rotulo>
            <ParagrafoInfo>{orientacao.testemunhas?.trim() || "--"}</ParagrafoInfo>

            <Rotulo>Pedidos relevantes da inicial</Rotulo>
            <ParagrafoInfo>{orientacao.pedidos?.trim() || "--"}</ParagrafoInfo>

            <Rotulo>Resumo da contestação</Rotulo>
            <ParagrafoInfo>{orientacao.resumo_contestacao?.trim() || "--"}</ParagrafoInfo>

            <Rotulo>Outras informações</Rotulo>
            <ParagrafoInfo>{orientacao.outras_informacoes?.trim() || "--"}</ParagrafoInfo>

            {Array.isArray(orientacao.observacoes) && orientacao.observacoes.length > 0 && (
              <ArquivosContainer>
                <Rotulo>Observações</Rotulo>
                {orientacao.observacoes.map((observacao: any, indiceObservacao: number) => (
                  <ValorDestacado key={indiceObservacao} style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {String(observacao)}
                  </ValorDestacado>
                ))}
              </ArquivosContainer>
            )}
          </Conteudo>
        );
      })}
    </Cartao>
  );
}
